import {
  getLocalStorageJson,
  setLocalStorageJson,
} from "@redotech/redo-web/utils/local-storage-wrapper";

const STORAGE_KEY = "redo.favorite_product_cache";
const CACHE_EXPIRY_DURATION = 10 * 60 * 1000; // 10 minutes in milliseconds

interface CacheData {
  map: Array<[string, boolean]>;
  lastUpdated: number;
}

interface ProductIdentifier {
  handle: string;
  variantId: string | null;
}

export function getAllFavoritedProducts(): ProductIdentifier[] {
  const cacheData: CacheData | null = getLocalStorageJson(STORAGE_KEY);

  if (!cacheData?.map) {
    return [];
  }

  const favoriteProducts: ProductIdentifier[] = [];
  for (const [key, value] of cacheData.map) {
    // If it's explicitly marked as not a favorite, ignore it
    if (value) {
      const { handle, variantId } = JSON.parse(key);
      const product: ProductIdentifier = { handle, variantId };
      favoriteProducts.push(product);
    }
  }

  return favoriteProducts;
}

// Returns undefined if the value is not found in the cache or the cache is expired
// Otherwise, returns the value from the cache
export function checkForFavoriteProductCache({
  handle,
  variantId,
  timeBeforeExpiration = CACHE_EXPIRY_DURATION,
  userLoggedIn = true,
}: {
  handle: string;
  variantId: string | null;
  timeBeforeExpiration?: number;
  userLoggedIn?: boolean;
}): boolean | undefined {
  const cacheData: CacheData | null = getLocalStorageJson(STORAGE_KEY);

  // If the user is not logged in, this cache is our source of truth. Do not expire the cache
  const shouldExpire =
    userLoggedIn &&
    cacheData &&
    timeSinceLastUpdate(cacheData) > timeBeforeExpiration;

  if (cacheData && !shouldExpire) {
    const favoriteProductCache = new Map<string, boolean>(cacheData.map);
    const key = JSON.stringify({ handle, variantId });
    return favoriteProductCache.get(key);
  }

  invalidateFavoriteProductCache();
  return undefined;
}

// Sets the value in the cache to false
export function removeFavoriteProductCache(
  handle: string,
  variantId: string | null,
  timeBeforeExpiration?: number,
): void {
  updateFavoriteProductCache(false, handle, variantId, timeBeforeExpiration);
}

// Sets the value in the cache to true
export function addFavoriteProductCache(
  handle: string,
  variantId: string | null,
  timeBeforeExpiration?: number,
): void {
  updateFavoriteProductCache(true, handle, variantId, timeBeforeExpiration);
}

// Must take in the current time so that we can mock the time in tests to test the expiration logic
function updateFavoriteProductCache(
  setAsFavorite: boolean,
  handle: string,
  variantId: string | null,
  currentTime: number = Date.now(),
): void {
  const cacheData: CacheData | null = getLocalStorageJson(STORAGE_KEY);
  const favoriteProductCache = new Map<string, boolean>(cacheData?.map ?? []);
  const key = JSON.stringify({ handle, variantId });

  favoriteProductCache.set(key, setAsFavorite);

  const newCacheData: CacheData = {
    map: Array.from(favoriteProductCache.entries()),
    lastUpdated: currentTime,
  };

  setLocalStorageJson(STORAGE_KEY, newCacheData);
}

export function invalidateFavoriteProductCache(): void {
  localStorage.removeItem(STORAGE_KEY);
}

function timeSinceLastUpdate(cacheData: CacheData): number {
  return Date.now() - cacheData.lastUpdated;
}
